/**
 * @file Maintenance close completion modal helpers.
 */

import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import {
  FIXED_TEXTAREA_CLASSES,
  NATIVE_INPUT_CLASSES,
  getNativeFieldWrapperHtml,
  validateNativeFormFields,
} from "../../shared/forms/nativeFieldStyles.js";
import {
  getDynamicFieldHtml,
  mountDynamicSelectControls,
} from "../../shared/forms/dynamicFields.js";
import {
  focusFirstRegisteredControlError,
  mergeRegisteredControlValues,
  validateRegisteredControls,
} from "../../shared/ui/controls/controlCore.js";
import { createDateControl } from "../../shared/ui/controls/dateControl.js";
import { getLocalDateString, formatDate } from "../../shared/utils/dateUtils.js";
import { formatCurrency } from "../../shared/utils/displayUtils.js";
import { sanitizeHTML } from "../../shared/utils/contentUtils.js";
import {
  mountMaintenanceTechnicianControl,
  renderMaintenanceTechnicianControlRoot,
} from "./maintenanceTechnicianControl.js";

function attrValue(value = "") {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function normalizeCurrencyInput(value) {
  if (value === null || value === undefined || value === "") {return null;}
  const number = typeof value === "number" ?
    value :
    Number(String(value).replace(/[$,\s]/g, ""));
  return Number.isFinite(number) ? number : null;
}

function getSelectedMaintenanceTypes(item = {}, config = {}) {
  const selectedTypeIds = new Set(
    (Array.isArray(item?.types) ? item.types : [])
      .map((typeId) => String(typeId || ""))
      .filter(Boolean),
  );
  if (selectedTypeIds.size === 0) {return [];}
  return (Array.isArray(config?.maintenanceTypes) ? config.maintenanceTypes : [])
    .filter((typeConfig) => selectedTypeIds.has(String(typeConfig?.id || "")));
}

function getRequiredRepairDynamicFields(item = {}, config = {}) {
  if (item?.stream !== "issue") {return [];}
  return getSelectedMaintenanceTypes(item, config)
    .flatMap((typeConfig) => Array.isArray(typeConfig?.repairDynamicFields) ?
      typeConfig.repairDynamicFields :
      [])
    .filter((field) => field?.required === true && field?.type !== "heading");
}

function getRepairDynamicFields(item = {}, config = {}) {
  if (item?.stream !== "issue") {return [];}
  return getSelectedMaintenanceTypes(item, config)
    .flatMap((typeConfig) => Array.isArray(typeConfig?.repairDynamicFields) ?
      typeConfig.repairDynamicFields :
      []);
}

function repairDetailsIncludeField(repairDetails, field = {}) {
  const label = String(field?.label || "").trim();
  if (!label) {return true;}
  const details = String(repairDetails || "");
  return details.includes(`**${label}**:`) ||
    details.toLowerCase().includes(label.toLowerCase());
}

function getDynamicFieldName(field = {}) {
  const label = field?.label || "Field";
  if (field?.name) {return field.name;}
  return `repair_${label.replace(/\s+/g, "_").toLowerCase()}`;
}

function getRepairDynamicRenderFields(fields = []) {
  return fields.map((field) => ({
    ...field,
    name: getDynamicFieldName(field),
  }));
}

function getMissingCompletionBlockers(candidate = {}, config = {}) {
  const blockers = [];
  if (!String(candidate.technician || "").trim()) {
    blockers.push({field: "technician", message: "Technician is required."});
  }
  if (normalizeCurrencyInput(candidate.cost) === null) {
    blockers.push({field: "cost", message: "Cost is required."});
  }
  if (!String(candidate.completed_on || "").trim()) {
    blockers.push({field: "completed_on", message: "Completed On is required."});
  }

  if (candidate.stream === "issue") {
    const missingRepairFields = getRequiredRepairDynamicFields(candidate, config)
      .filter((field) => !repairDetailsIncludeField(candidate.repair_details, field));
    missingRepairFields.forEach((field) => {
      blockers.push({
        field: `repair_details.${field.id || field.label || "field"}`,
        message: `${field.label || "Repair field"} is required.`,
      });
    });
  }
  return blockers;
}

function getCompletionCandidate(item = {}, completionData = {}) {
  return {
    ...item,
    ...completionData,
    cost: Object.prototype.hasOwnProperty.call(completionData, "cost") ?
      normalizeCurrencyInput(completionData.cost) :
      item?.cost,
  };
}

function getRepairFieldMarkdown(form, fields = []) {
  const lines = [];
  fields.forEach((field) => {
    const name = getDynamicFieldName(field);
    const input = form.querySelector(`[name="${CSS.escape(name)}"]`);
    if (!input) {return;}

    let value = input.type === "checkbox" ?
      (input.checked ? "Yes" : "") :
      input.value;
    if ((input.type === "date" || input.type === "datetime-local") && value) {
      value = formatDate(value);
    }
    if (!String(value || "").trim()) {return;}

    const label = String(field?.label || name).trim();
    const valueLines = String(value).split("\n");
    lines.push(`**${label}**: ${valueLines[0]}`);
    valueLines.slice(1).forEach((line) => {
      lines.push(line);
    });
  });
  return lines.join("\n").trim();
}

function setModalError(message) {
  const element = document.querySelector("[data-maintenance-close-error]");
  if (!element) {return;}
  if (!message) {
    element.classList.add("hidden");
    element.textContent = "";
    return;
  }
  element.classList.remove("hidden");
  element.textContent = message;
}

function formatBlockerMessage(blockers = []) {
  return blockers
    .map((blocker) => blocker?.message)
    .filter(Boolean)
    .join(" ");
}

function bindCloseCostInput(form) {
  const input = form?.querySelector?.('input[name="cost"]');
  if (!input) {return;}
  let rawValue = input.value || "";
  input.dataset.currencyRawValue = rawValue;
  input.addEventListener("input", (event) => {
    let value = event.target.value.replace(/[^0-9.]/g, "");
    if (value.split(".").length > 2) {
      value = value.substring(0, value.lastIndexOf("."));
    }
    rawValue = value;
    input.dataset.currencyRawValue = rawValue;
    event.target.value = value;
  });
  input.addEventListener("focus", () => {
    input.value = rawValue || "";
  });
  input.addEventListener("blur", () => {
    const formatted = formatCurrency(rawValue);
    input.value = formatted || rawValue || "";
  });
}

function setRepairCheckboxError(input, message = "") {
  const label = input?.closest?.("[data-native-checkbox-label]");
  const marker = label?.querySelector?.("[data-native-checkbox-marker]");
  if (!label || !marker) {return;}
  let error = label.parentElement?.querySelector?.(
    `[data-repair-checkbox-error="${CSS.escape(input.name)}"]`,
  );
  if (!error) {
    error = document.createElement("p");
    error.dataset.repairCheckboxError = input.name;
    error.className = "mt-1 text-xs font-medium text-red-600";
    label.insertAdjacentElement("afterend", error);
  }
  error.textContent = message;
  error.classList.toggle("hidden", !message);
  input.setAttribute("aria-invalid", String(Boolean(message)));
  marker.style.borderColor = message ? "#dc2626" : "";
}

function validateRequiredRepairCheckboxes(form, fields = []) {
  let firstInvalid = null;
  fields
    .filter((field) => field?.required === true &&
      ["yes_no", "checkbox", "boolean"].includes(field?.type))
    .forEach((field) => {
      const name = getDynamicFieldName(field);
      const input = form.querySelector(`[name="${CSS.escape(name)}"]`);
      if (!input) {return;}
      const valid = input.checked === true;
      setRepairCheckboxError(input, valid ? "" : "Required");
      if (!valid && !firstInvalid) {
        firstInvalid = input;
      }
    });

  if (firstInvalid) {
    firstInvalid.focus({preventScroll: true});
    firstInvalid.scrollIntoView({block: "center", behavior: "smooth"});
    return false;
  }
  return true;
}

function mountRepairDateControls(form, fields = []) {
  fields
    .filter((field) => field?.type === "date" || field?.type === "datetime-local")
    .forEach((field) => {
      const name = getDynamicFieldName(field);
      const input = form.querySelector(`[name="${CSS.escape(name)}"]`);
      const root = input?.parentElement;
      if (!input || !root) {return;}

      createDateControl({
        root,
        name,
        nativeInputName: name,
        type: field.type,
        value: input.value || "",
        required: Boolean(field.required),
        label: field.label || name,
        mirror: false,
      });
    });
}

/**
 * Returns whether the close completion modal is required for this item.
 * @param {object} params Check params.
 * @param {object} params.item Maintenance item.
 * @param {object} params.config App config.
 * @returns {boolean} True when required completion data is missing.
 */
export function shouldOpenMaintenanceCloseModal({item = {}, config = {}} = {}) {
  return getMissingCompletionBlockers(item, config).length > 0;
}

/**
 * Converts backend blocking responses into user-facing text.
 * @param {object} response Backend response.
 * @returns {string} Message text.
 */
export function getMaintenanceCloseFailureMessage(response = {}) {
  return formatBlockerMessage(response?.blockers || []) ||
    response?.message ||
    "Maintenance close validation failed.";
}

/**
 * Opens the Maintenance completion modal.
 * @param {object} params Modal params.
 * @param {object} params.item Maintenance item.
 * @param {object} params.config App config.
 * @param {Function} params.onCancel Cancel callback.
 * @param {Function} params.onSubmit Submit callback.
 * @returns {void}
 */
export function openMaintenanceCloseModal({
  item = {},
  config = {},
  onCancel,
  onSubmit,
} = {}) {
  const repairFields = getRepairDynamicRenderFields(
    getRepairDynamicFields(item, config),
  );
  const repairFieldsHeading = repairFields.every((field) => {
    return field?.type === "heading" || field?.required === true;
  }) ? "Required Repair Fields" : "Repair Fields";
  const costValue = typeof item?.cost === "number" ? String(item.cost) : "";
  const completedOnValue = item?.completed_on || getLocalDateString();

  const bodyHtml = `
    <form data-maintenance-close-form class="space-y-4 text-left">
      <div data-maintenance-close-error class="hidden rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"></div>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        ${getNativeFieldWrapperHtml("Technician", `
          ${renderMaintenanceTechnicianControlRoot()}
        `, {required: true})}
        ${getNativeFieldWrapperHtml("Cost", `
          <input type="text" name="cost" value="${attrValue(costValue)}" class="${NATIVE_INPUT_CLASSES} currency-input" placeholder="$" inputmode="decimal" required>
        `, {required: true})}
        ${getNativeFieldWrapperHtml("Completed On", `
          <div data-maintenance-close-date-control="completed_on"></div>
        `, {required: true})}
      </div>
      ${item?.stream === "issue" ? `
        ${repairFields.length > 0 ? `
          <div class="rounded-lg bg-blue-100/40 p-4 dark:bg-blue-900/20" data-maintenance-repair-fields>
            <h5 class="mb-3 text-sm font-semibold text-blue-800 dark:text-blue-300">${repairFieldsHeading}</h5>
            <div class="grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-4">
              ${repairFields.map(getDynamicFieldHtml).join("")}
            </div>
          </div>
        ` : ""}
        ${getNativeFieldWrapperHtml("Repair Details", `
          <textarea name="repair_details" class="${FIXED_TEXTAREA_CLASSES}" rows="4">${sanitizeHTML(item?.repair_details || "")}</textarea>
        `, {className: "md:col-span-2"})}
      ` : ""}
    </form>
  `;

  openConfirmationModal(
    "Complete Maintenance",
    bodyHtml,
    async () => {
      const form = document.querySelector("[data-maintenance-close-form]");
      if (!form) {return false;}
      setModalError("");
      const nativeValid = validateNativeFormFields(form);
      const registeredValidation = validateRegisteredControls(form);
      const repairCheckboxesValid = validateRequiredRepairCheckboxes(
        form,
        repairFields,
      );
      if (!nativeValid || !registeredValidation.valid || !repairCheckboxesValid) {
        if (nativeValid) {
          focusFirstRegisteredControlError(registeredValidation);
        }
        return false;
      }
      const formData = new FormData(form);
      const data = mergeRegisteredControlValues(
        Object.fromEntries(formData.entries()),
        form,
      );
      const costInput = form.querySelector('input[name="cost"]');
      const repairFieldMarkdown = getRepairFieldMarkdown(form, repairFields);
      const repairDetails = [
        String(data.repair_details || "").trim(),
        repairFieldMarkdown,
      ].filter(Boolean).join("\n\n");
      const completionData = {
        technician: data.technician || "",
        cost: costInput?.dataset?.currencyRawValue ?? data.cost ?? "",
        completed_on: data.completed_on || "",
      };
      if (item?.stream === "issue") {
        completionData.repair_details = repairDetails;
      }

      const blockers = getMissingCompletionBlockers(
        getCompletionCandidate(item, completionData),
        config,
      );
      if (blockers.length > 0) {
        setModalError(formatBlockerMessage(blockers));
        return false;
      }

      try {
        const result = await onSubmit?.(completionData);
        if (result === false) {return false;}
        return true;
      } catch (error) {
        setModalError(error?.message || "Maintenance could not be closed.");
        return false;
      }
    },
    false,
    "Close Maintenance",
    onCancel,
    "Closing...",
    "Cancel",
    {
      keepOpenOnFalse: true,
      closeOnError: false,
      maxWidth: "2xl",
    },
  );

  const form = document.querySelector("[data-maintenance-close-form]");
  bindCloseCostInput(form);
  const completedOnRoot = form?.querySelector(
    '[data-maintenance-close-date-control="completed_on"]',
  );
  if (completedOnRoot) {
    createDateControl({
      root: completedOnRoot,
      name: "completed_on",
      nativeInputName: "completed_on",
      type: "date",
      value: completedOnValue,
      required: true,
      mirror: false,
    });
  }
  const repairFieldsContainer = form?.querySelector("[data-maintenance-repair-fields]");
  if (repairFieldsContainer) {
    mountDynamicSelectControls(repairFieldsContainer, {
      fields: repairFields,
    });
    mountRepairDateControls(form, repairFields);
    repairFieldsContainer
      .querySelectorAll('input[type="checkbox"]')
      .forEach((input) => {
        input.addEventListener("change", () => setRepairCheckboxError(input, ""));
      });
  }
  mountMaintenanceTechnicianControl({
    root: form?.querySelector("[data-maintenance-technician-control]"),
    appData: {config},
    value: item?.technician || "",
    required: true,
  });
}
